/* NEW: UI_REVAMP */
import React from 'react';
import {
	Accordion,
	AccordionButton,
	AccordionItem,
	AccordionPanel,
	Box,
	Flex,
	Text,
} from '@chakra-ui/react';
import ImgComp from '../../../utils/ImgComp';
import { currencyFormat } from '../../../utils/numberFormat';
import GradientText from '../../common/GradientText';
import { RECORD_GRADIENTS, RECORD_TEXT_COLORS } from '../../../styles/recordUiTokens';
import useIsMobileView from '../../../hooks/useIsMobileView';

const BET_TABLE_COLUMNS = [
	{
		key: 'numbers',
		label: 'Numbers',
		desktopWidth: '31%',
		mobileFlex: '1.4',
		textAlign: 'left',
	},
	{
		key: 'betPrice',
		label: 'Bet Price (PHP)',
		desktopWidth: '17%',
		mobileFlex: '1',
		textAlign: 'right',
	},
	{
		key: 'odds',
		label: 'Odds',
		desktopWidth: '17%',
		mobileFlex: '0.7',
		textAlign: 'right',
	},
	{
		key: 'result',
		label: 'Result',
		desktopWidth: '17%',
		mobileFlex: '0.9',
		textAlign: 'right',
	},
	{
		key: 'winAmount',
		label: 'Win Amount (PHP)',
		desktopWidth: '18%',
		mobileFlex: '1',
		textAlign: 'right',
	},
];

const STRAIGHT_BET_TYPE_STYLE = {
	background: RECORD_GRADIENTS.straightBadge,
	color: '#000000',
};

const RAMBLE_BET_TYPE_STYLE = {
	background: RECORD_GRADIENTS.rambleBadge,
	color: RECORD_TEXT_COLORS.white,
};

export const groupHistoryBetsByGameAndType = (bets = []) => {
	const grouped = new Map();

	bets.forEach((bet, index) => {
		const key = `${bet?.gameType || 'Unknown'}-${bet?.ramble ? 'Ramble' : 'Straight'}`;

		if (!grouped.has(key)) {
			grouped.set(key, {
				id: key,
				gameType: bet?.gameType || 'Unknown',
				betType: bet?.ramble ? 'Ramble' : 'Straight',
				items: [],
			});
		}

		grouped.get(key).items.push({
			...bet,
			rowKey: `${key}-${bet?.bettingId || bet?.ticketBettingId || index}`,
		});
	});

	return Array.from(grouped.values());
};

const isWinningBet = (bet, showText) => showText && Number(bet?.winningPrice || 0) > 0;

const formatHistoryBetRow = (bet, showText) => {
	const isWinningRow = isWinningBet(bet, showText);

	return {
		rowKey: bet.rowKey,
		numbersText: bet.number?.map((num) => num.digit).join(' '),
		hasLuckyPick: bet.lp,
		betPriceText: currencyFormat(Number(bet.betPrice || 0)),
		oddsText: String(Number(bet.winRate || 0)),
		resultText: isWinningRow ? 'Win' : '-',
		winAmountText: isWinningRow ? currencyFormat(Number(bet.winningPrice || 0)) : '-',
		isWinningRow,
	};
};

const getGroupSummary = (items = [], showText) => {
	const totalBet = currencyFormat(items.reduce((sum, item) => sum + Number(item.betPrice || 0), 0));
	const winCount = items.filter((item) => Number(item.winningPrice || 0) > 0).length;
	const totalWinningValue = items.reduce((sum, item) => sum + Number(item.winningPrice || 0), 0);

	return {
		totalBet,
		totalResult: showText && winCount > 0 ? `${winCount} Win` : '-',
		totalWinAmount: showText && totalWinningValue > 0 ? currencyFormat(totalWinningValue) : '-',
	};
};

const getBetTypeStyle = (betType) =>
	betType === 'Straight' ? STRAIGHT_BET_TYPE_STYLE : RAMBLE_BET_TYPE_STYLE;

const BetTypeBadge = ({ betType, isMobileView }) => {
	const styles = getBetTypeStyle(betType);

	return (
		<Box
			display='inline-flex'
			alignItems='center'
			justifyContent='center'
			minHeight='24px'
			padding='4px 12px'
			borderRadius='4px'
			fontSize={isMobileView ? '14px' : '16px'}
			fontWeight='600'
			lineHeight='100%'
			background={styles.background}
			color={styles.color}
		>
			{betType}
		</Box>
	);
};

const BetNumberCell = ({
	numbersText,
	hasLuckyPick,
	iconSize = '16px',
	gap = '6px',
	textFontSize,
}) => {
	return (
		<Flex alignItems='center' gap={gap} minWidth='0'>
			<Text
				fontSize={textFontSize}
				fontWeight='500'
				lineHeight='20px'
				color={RECORD_TEXT_COLORS.primary}
			>
				{numbersText}
			</Text>
			{hasLuckyPick ? <ImgComp svgName='lucky' width={iconSize} height={iconSize} /> : null}
		</Flex>
	);
};

const HistoryBetTableHeader = ({ isMobile = false }) => {
	if (isMobile) {
		return (
			<Flex
				fontSize='10px'
				fontWeight='500'
				color={RECORD_TEXT_COLORS.muted}
				lineHeight='100%'
				letterSpacing='0'
				marginBottom='2px'
			>
				{BET_TABLE_COLUMNS.map((column) => (
					<Text key={column.key} flex={column.mobileFlex} textAlign={column.textAlign}>
						{column.label}
					</Text>
				))}
			</Flex>
		);
	}

	return (
		<Box as='thead'>
			<Box as='tr'>
				{BET_TABLE_COLUMNS.map((column) => (
					<Box key={column.key} as='th' textAlign={column.textAlign}>
						{column.label}
					</Box>
				))}
			</Box>
		</Box>
	);
};

const HistoryBetDesktopTable = ({ items, showText }) => {
	const summary = getGroupSummary(items, showText);

	return (
		<Box
			as='table'
			width='100%'
			display={{ base: 'none', lg: 'table' }}
			sx={{
				tableLayout: 'fixed',
				tfoot: {
					td: {
						padding: '8px 4px',
					},
				},
				th: {
					paddingBottom: '4px',
					textTransform: 'none',
					fontSize: '14px',
					fontWeight: '500',
					lineHeight: '100%',
					color: RECORD_TEXT_COLORS.secondary,
				},
				td: {
					fontSize: '16px',
					fontWeight: '600',
					color: RECORD_TEXT_COLORS.primary,
					padding: '4px 4px',
					verticalAlign: 'top',
					whiteSpace: 'nowrap',
					lineHeight: '100%',
				},
			}}
		>
			<Box as='colgroup'>
				{BET_TABLE_COLUMNS.map((column) => (
					<Box key={column.key} as='col' width={column.desktopWidth} />
				))}
			</Box>
			<HistoryBetTableHeader />
			<Box as='tbody'>
				{items.map((bet) => {
					const row = formatHistoryBetRow(bet, showText);

					return (
						<Box as='tr' key={row.rowKey}>
							<Box as='td'>
								<BetNumberCell
									numbersText={row.numbersText}
									hasLuckyPick={row.hasLuckyPick}
									iconSize='16px'
									gap='6px'
									textFontSize='14px'
								/>
							</Box>
							<Box as='td' textAlign='right'>
								{row.betPriceText}
							</Box>
							<Box as='td' textAlign='right'>
								{row.oddsText}
							</Box>
							<Box as='td' textAlign='right' color={RECORD_TEXT_COLORS.primary}>
								{row.isWinningRow ? (
									<GradientText fontSize='16px' fontWeight='600'>
										{row.resultText}
									</GradientText>
								) : (
									row.resultText
								)}
							</Box>
							<Box as='td' textAlign='right' color={RECORD_TEXT_COLORS.primary}>
								{row.isWinningRow ? (
									<GradientText fontSize='16px' fontWeight='600'>
										{row.winAmountText}
									</GradientText>
								) : (
									row.winAmountText
								)}
							</Box>
						</Box>
					);
				})}
			</Box>
			<Box as='tfoot'>
				<Box as='tr'>
					<Box as='td' borderTop='1px solid #ECD1DA'>
						Total
					</Box>
					<Box as='td' textAlign='right' borderTop='1px solid #ECD1DA'>
						{summary.totalBet}
					</Box>
					<Box as='td' borderTop='1px solid #ECD1DA' />
					<Box as='td' textAlign='right' borderTop='1px solid #ECD1DA'>
						{showText ? (
							<GradientText fontSize='16px' fontWeight='600'>
								{summary.totalResult}
							</GradientText>
						) : (
							summary.totalResult
						)}
					</Box>
					<Box as='td' textAlign='right' borderTop='1px solid #ECD1DA'>
						{showText ? (
							<GradientText fontSize='16px' fontWeight='600'>
								{summary.totalWinAmount}
							</GradientText>
						) : (
							summary.totalWinAmount
						)}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

const MobileBetTableRow = ({ row }) => {
	return (
		<Box>
			<Flex fontSize='14px' fontWeight='600' color='#000000' alignItems='flex-start'>
				<Box flex={BET_TABLE_COLUMNS[0].mobileFlex}>
					<BetNumberCell
						numbersText={row.numbersText}
						hasLuckyPick={row.hasLuckyPick}
						iconSize='14px'
						gap='4px'
						textFontSize='14px'
					/>
				</Box>
				<Text flex='1' textAlign='right'>
					{row.betPriceText}
				</Text>
				<Text flex='0.7' textAlign='right'>
					{row.oddsText}
				</Text>
				<Box flex='0.9' textAlign='right'>
					{row.isWinningRow ? (
						<GradientText fontSize='14px' fontWeight='600'>
							{row.resultText}
						</GradientText>
					) : (
						<Text>{row.resultText}</Text>
					)}
				</Box>
				<Box flex='1' textAlign='right'>
					{row.isWinningRow ? (
						<GradientText fontSize='14px' fontWeight='600'>
							{row.winAmountText}
						</GradientText>
					) : (
						<Text>{row.winAmountText}</Text>
					)}
				</Box>
			</Flex>
		</Box>
	);
};

const MobileBetTableTotal = ({ totalBet, totalResult, totalWinAmount, isWinningTotal }) => {
	return (
		<Flex
			borderTop='1px solid #ECD1DA'
			paddingTop='4px'
			marginTop='4px'
			fontSize='14px'
			fontWeight='600'
			color='#000000'
			alignItems='center'
		>
			<Text flex='1.4'>Total</Text>
			<Text flex='1' textAlign='right'>
				{totalBet}
			</Text>
			<Text flex='0.7' textAlign='right'></Text>
			<Box flex='0.9' textAlign='right'>
				{isWinningTotal ? (
					<GradientText fontSize='14px' fontWeight='600'>
						{totalResult}
					</GradientText>
				) : (
					<Text>{totalResult}</Text>
				)}
			</Box>
			<Box flex='1' textAlign='right'>
				{isWinningTotal ? (
					<GradientText fontSize='14px' fontWeight='600'>
						{totalWinAmount}
					</GradientText>
				) : (
					<Text>{totalWinAmount}</Text>
				)}
			</Box>
		</Flex>
	);
};

const HistoryBetMobileTable = ({ items, showText }) => {
	const summary = getGroupSummary(items, showText);

	return (
		<Box display={{ base: 'block', lg: 'none' }}>
			<HistoryBetTableHeader isMobile />
			{items.map((bet) => {
				const row = formatHistoryBetRow(bet, showText);

				return (
					<Box key={row.rowKey} paddingY='4px'>
						<MobileBetTableRow row={row} />
					</Box>
				);
			})}
			<MobileBetTableTotal
				totalBet={summary.totalBet}
				totalResult={summary.totalResult}
				totalWinAmount={summary.totalWinAmount}
				isWinningTotal={showText}
			/>
		</Box>
	);
};

const HistoryBetAccordionHeader = ({ gameType, betType, isExpanded, isMobileView }) => {
	return (
		<AccordionButton
			padding='6px 8px'
			_hover={{ background: 'transparent' }}
			justifyContent='space-between'
			alignItems='center'
		>
			<Flex alignItems='center' gap='8px'>
				<Box width='24px' height='24px' flexShrink={0}>
					<ImgComp svgName={String(gameType || '').toLowerCase()} width='24px' height='24px' />
				</Box>
				<Text
					fontSize={isMobileView ? '14px' : '16px'}
					fontWeight='600'
					color='#000000'
					lineHeight='100%'
				>
					{gameType}
				</Text>
				<BetTypeBadge betType={betType} isMobileView={isMobileView} />
			</Flex>
			<Box
				width='24px'
				height='24px'
				transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
				transition='transform 0.2s ease'
			>
				<ImgComp svgName='chevron-down' width='24px' height='24px' />
			</Box>
		</AccordionButton>
	);
};

const HistoryBetAccordionList = ({ groups, showText }) => {
	const isMobileView = useIsMobileView();
	if (!groups?.length) return null;

	return (
		<Accordion allowMultiple defaultIndex={groups.map((_, index) => index)} marginTop='6px'>
			{groups.map((group) => (
				<Box
					key={group.id}
					padding='1px'
					borderRadius='8px'
					background='linear-gradient(180deg, #F5D7A9 0%, #E28E10 10.42%, #E3CA89 25%, #E2A74C 69.79%, #4D3A1A 85.42%, #F5D7A9 100%)'
					boxShadow='0 3px 8px rgba(100, 72, 180, 0.18)'
					marginBottom='6px'
				>
					<AccordionItem
						border='none'
						borderRadius='7px'
						background='linear-gradient(180deg, #FDFEFF 0%, #EFF6FF 100%)'
						overflow='hidden'
					>
						{({ isExpanded }) => (
							<>
								<HistoryBetAccordionHeader
									gameType={group.gameType}
									betType={group.betType}
									isExpanded={isExpanded}
									isMobileView={isMobileView}
								/>

								<AccordionPanel padding='0'>
									<Box
										width='100%'
										background='linear-gradient(180deg, #FDFEFF 0%, #EFF6FF 100%)'
										borderTop='1px solid #F2F4F7'
										padding='6px 8px 4px'
									>
										{isMobileView ? (
											<HistoryBetMobileTable items={group.items} showText={showText} />
										) : (
											<HistoryBetDesktopTable items={group.items} showText={showText} />
										)}
									</Box>
								</AccordionPanel>
							</>
						)}
					</AccordionItem>
				</Box>
			))}
		</Accordion>
	);
};

export default HistoryBetAccordionList;
